// GENERATED from messaging-platform/packages/chat-core/src/file-upload.ts. Do not edit here: edit there, then run scripts/sync-chat-core.cjs.
import { HubRequestError, type AttachmentView } from './hub-types';

/**
 * Sending files through the messaging hub, the same way in every app (clinical, Connect, the referrals and support
 * dashboards; "support file sending in a robust way across the whole ecosystem", 2026-09-19). No rxjs, no framework:
 * the support dashboard (React) syncs this file too.
 *
 *   checkFile      refuses early what the hub would refuse (type, size), with the words to show
 *   prepareImage   shrinks a large photo and turns HEIC into JPEG where the browser can decode it (optional)
 *   uploadToHub    the hub's resumable upload: chunks (4 MiB), each retried with backoff, progress as it goes, cancel
 *                  with an AbortSignal; after a failure, the same file resumes from the chunks the hub already has
 *
 * The hub checks everything again (type by content, size) and strips photo metadata; these checks only spare the
 * user a wait for a refusal.
 */

/** The hub's limits (messaging-platform apps/api/src/attachments.ts). */
export const MAX_FILE_BYTES = 100 * 1024 * 1024;
export const MAX_FILES_PER_MESSAGE = 10;

/** Accepted types, and the extensions that stand for them when the browser gives no type (common on Windows). */
const TYPES_BY_EXTENSION: Record<string, string> = {
  png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', gif: 'image/gif', webp: 'image/webp', heic: 'image/heic', heif: 'image/heic',
  pdf: 'application/pdf', txt: 'text/plain', csv: 'text/csv',
  doc: 'application/msword', docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xls: 'application/vnd.ms-excel', xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  ppt: 'application/vnd.ms-powerpoint', pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  mp3: 'audio/mpeg', m4a: 'audio/mp4', weba: 'audio/webm', ogg: 'audio/ogg', oga: 'audio/ogg', wav: 'audio/wav',
  mp4: 'video/mp4', webm: 'video/webm', mov: 'video/quicktime',
};
const ALIASES: Record<string, string> = { 'image/jpg': 'image/jpeg', 'image/heif': 'image/heic', 'audio/x-wav': 'audio/wav', 'audio/wave': 'audio/wav', 'audio/mp3': 'audio/mpeg', 'audio/x-m4a': 'audio/mp4' };
const ACCEPTED = new Set(Object.values(TYPES_BY_EXTENSION));

/** For `<input type="file" accept>`: every accepted extension and type. */
export const FILE_ACCEPT = [...Object.keys(TYPES_BY_EXTENSION).map((e) => `.${e}`), ...ACCEPTED].join(',');

/** The type the hub will be told: the browser's (normalised), or the one the extension stands for. */
export function fileTypeOf(file: Blob, fileName: string): string {
  const declared = (file.type || '').toLowerCase().split(';')[0]!.trim();
  const normal = ALIASES[declared] ?? declared;
  if (normal && ACCEPTED.has(normal)) return normal;
  const ext = fileName.toLowerCase().split('.').pop() ?? '';
  // audio/webm recorded in the browser often arrives as video/webm; the extension settles it
  return TYPES_BY_EXTENSION[ext] ?? (normal || 'application/octet-stream');
}

/** null when the file may be sent; otherwise an error code (fileErrorText has the words). */
export function checkFile(file: Blob, fileName: string): 'FILE_TYPE' | 'FILE_TOO_LARGE' | 'FILE_EMPTY' | null {
  if (!ACCEPTED.has(fileTypeOf(file, fileName))) return 'FILE_TYPE';
  if (file.size <= 0) return 'FILE_EMPTY';
  if (file.size > MAX_FILE_BYTES) return 'FILE_TOO_LARGE';
  return null;
}

/** What to tell a person when a file could not be sent. */
export function fileErrorText(code: string, fileName?: string): string {
  const name = fileName ? `“${fileName}”` : 'The file';
  switch (code) {
    case 'FILE_TYPE': return `${name} can't be sent: send photos, PDFs, Office documents, text, audio or video.`;
    case 'FILE_TOO_LARGE': return `${name} is larger than ${MAX_FILE_BYTES / 1024 / 1024} MB.`;
    case 'FILE_EMPTY': return `${name} is empty.`;
    case 'TOO_MANY_FILES': return `At most ${MAX_FILES_PER_MESSAGE} files per message.`;
    case 'CANCELLED': return 'Upload cancelled.';
    case 'NOT_CONNECTED': return 'Not connected to chat. The upload continues when the connection is back; try again.';
    case 'READ_ONLY': return 'This conversation no longer accepts messages.';
    case 'EXTERNAL_REFERRAL_NO_FILES': return 'Files cannot be sent in a referral chat with a clinician outside MedicAI.';
    case 'FILE_GRANT_REQUIRED':
    case 'FILE_GRANT_INVALID': return 'Files could not be sent in this chat just now. Try again.';
    case 'DOCUMENT_ALLOWANCE_USED': return 'Every document this referral allows has been sent. The practice can refer the patient again to send more.';
    default: return `${name} could not be uploaded. Try again: it continues where it stopped.`;
  }
}

export interface UploadProgress {
  sentBytes: number;
  totalBytes: number;
}

export interface UploadOptions {
  onProgress?: (p: UploadProgress) => void;
  signal?: AbortSignal;
  /** retries per chunk before giving up (default 6: about a minute of backoff) */
  retries?: number;
  /** the first backoff (doubling each time, at most 15 s); default 1000 ms */
  backoffMs?: number;
}

/** What the upload needs from the hub connection. */
export interface UploadTransport {
  request<T>(event: string, payload: unknown): Promise<T>;
  baseUrl(): string | null;
}

interface Started {
  uploadPath: string;
  chunkBytes: number;
  chunks: number;
}

/** Uploads started for a file and not finished: a retry of the same file resumes (per page, in memory). */
const inFlight = new WeakMap<Blob, { conversationId: string; started: Started }>();

const sleep = (ms: number, signal?: AbortSignal) => new Promise<void>((resolve, reject) => {
  const t = setTimeout(resolve, ms);
  signal?.addEventListener('abort', () => {
    clearTimeout(t);
    reject(new HubRequestError('CANCELLED'));
  }, { once: true });
});

/** 4xx other than conflict and throttling will not get better by trying again. */
const permanent = (status: number) => status >= 400 && status < 500 && status !== 408 && status !== 409 && status !== 429;

async function errorCodeOf(res: Response): Promise<string> {
  const body = (await res.json().catch(() => null)) as { error?: string } | null;
  return body?.error ?? (res.status === 413 ? 'FILE_TOO_LARGE' : res.status === 415 ? 'FILE_TYPE' : 'UPLOAD_FAILED');
}

/** One PUT with byte progress (XMLHttpRequest in browsers; fetch elsewhere). Resolves the status and body text. */
function put(url: string, body: Blob, onBytes: (n: number) => void, signal?: AbortSignal): Promise<Response> {
  if (typeof XMLHttpRequest === 'undefined') {
    return fetch(url, { method: 'PUT', body, headers: { 'content-type': 'application/octet-stream' }, ...(signal ? { signal } : {}) })
      .then((res) => {
        if (res.ok) onBytes(body.size);
        return res;
      });
  }
  return new Promise<Response>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    xhr.setRequestHeader('content-type', 'application/octet-stream');
    xhr.upload.onprogress = (e) => onBytes(e.loaded);
    xhr.onload = () => resolve(new Response(xhr.responseText || null, { status: xhr.status }));
    xhr.onerror = () => reject(new TypeError('network error'));
    xhr.ontimeout = () => reject(new TypeError('timeout'));
    xhr.timeout = 120_000;
    signal?.addEventListener('abort', () => xhr.abort(), { once: true });
    xhr.onabort = () => reject(new HubRequestError('CANCELLED'));
    xhr.send(body);
  });
}

/**
 * Uploads one file into a conversation, resumably. Resolves the staged file (send it with the message's
 * attachmentIds). Rejects with a HubRequestError: FILE_TYPE / FILE_TOO_LARGE / FILE_EMPTY (refused), CANCELLED, or
 * UPLOAD_FAILED / NOT_CONNECTED (try again: the same Blob resumes).
 */
export async function uploadToHub(
  hub: UploadTransport,
  conversationId: string,
  file: Blob,
  fileName: string,
  options: UploadOptions = {},
): Promise<AttachmentView> {
  const refused = checkFile(file, fileName);
  if (refused) throw new HubRequestError(refused);
  const base = hub.baseUrl();
  if (!base) throw new HubRequestError('NOT_CONNECTED');
  const { signal, onProgress } = options;
  const retries = options.retries ?? 6;
  if (signal?.aborted) throw new HubRequestError('CANCELLED');

  // Resume an interrupted upload of this very file, or start one.
  let started: Started | null = null;
  let have = new Set<number>();
  const previous = inFlight.get(file);
  if (previous && previous.conversationId === conversationId) {
    const res = await fetch(`${base}${previous.started.uploadPath}`).catch(() => null);
    if (res?.ok) {
      started = previous.started;
      have = new Set(((await res.json()) as { received: number[] }).received);
    }
  }
  if (!started) {
    started = await hub.request<Started>('attach:start', { conversationId, fileName, contentType: fileTypeOf(file, fileName), sizeBytes: file.size });
    inFlight.set(file, { conversationId, started });
  }
  const s = started;
  const url = `${base}${s.uploadPath}`;
  const onAbort = () => void fetch(url, { method: 'DELETE' }).catch(() => undefined);
  signal?.addEventListener('abort', onAbort, { once: true });

  const sizeOf = (i: number) => Math.min(s.chunkBytes, file.size - i * s.chunkBytes);
  let done = [...have].reduce((n, i) => n + sizeOf(i), 0);
  const report = (current: number) => onProgress?.({ sentBytes: Math.min(file.size, done + current), totalBytes: file.size });
  report(0);

  /** Tries `attempt` until it succeeds, backing off 1 s, 2 s, 4 s... (at most 15 s) on network errors and 5xx. */
  const withRetry = async (attempt: () => Promise<Response>): Promise<Response> => {
    for (let n = 0; ; n++) {
      if (signal?.aborted) throw new HubRequestError('CANCELLED');
      let res: Response | null = null;
      try {
        res = await attempt();
      } catch (e) {
        if (e instanceof HubRequestError) throw e; // cancelled
      }
      if (res && (res.ok || permanent(res.status))) return res;
      if (n >= retries) throw new HubRequestError('UPLOAD_FAILED');
      await sleep(Math.min(15_000, (options.backoffMs ?? 1000) * 2 ** n), signal);
    }
  };

  try {
    for (let i = 0; i < s.chunks; i++) {
      if (have.has(i)) continue;
      const part = file.slice(i * s.chunkBytes, i * s.chunkBytes + sizeOf(i));
      const res = await withRetry(() => put(`${url}/${i}`, part, (n) => report(n), signal));
      if (!res.ok) {
        if (res.status === 404) inFlight.delete(file); // the ticket expired: a retry starts over
        throw new HubRequestError(res.status === 404 ? 'UPLOAD_FAILED' : await errorCodeOf(res));
      }
      done += part.size;
      report(0);
    }
    const res = await withRetry(() => fetch(`${url}/complete`, { method: 'POST' }));
    if (!res.ok) {
      const code = await errorCodeOf(res);
      if (res.status === 404 || code !== 'UPLOAD_INCOMPLETE') inFlight.delete(file);
      throw new HubRequestError(code === 'UPLOAD_INCOMPLETE' ? 'UPLOAD_FAILED' : code);
    }
    inFlight.delete(file);
    return (await res.json()) as AttachmentView;
  } catch (e) {
    if (e instanceof HubRequestError && (e.code === 'CANCELLED' || e.code === 'FILE_TYPE' || e.code === 'FILE_TOO_LARGE')) inFlight.delete(file);
    throw e;
  } finally {
    signal?.removeEventListener('abort', onAbort);
  }
}

// ── photos ──────────────────────────────────────────────────────────────────────────

export interface PreparedFile {
  file: Blob;
  fileName: string;
}

/**
 * A photo ready to send: larger than `maxDimension` (or a HEIC the browser can decode) is redrawn as a JPEG, upright,
 * at most `maxDimension` on its longest side. Everything else (and anything the browser cannot decode) is returned
 * unchanged. Re-encoding also drops the photo's metadata; the hub strips it anyway.
 */
export async function prepareImage(file: Blob, fileName: string, options: { maxDimension?: number; quality?: number } = {}): Promise<PreparedFile> {
  const type = fileTypeOf(file, fileName);
  const same = { file, fileName };
  if (!['image/jpeg', 'image/png', 'image/webp', 'image/heic'].includes(type)) return same;
  if (typeof createImageBitmap === 'undefined' || typeof document === 'undefined') return same;
  const max = options.maxDimension ?? 2560;
  let bitmap: ImageBitmap;
  try {
    // 'from-image' (upright, per EXIF) is newer than some apps' DOM typings
    bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' } as unknown as ImageBitmapOptions);
  } catch {
    return same; // HEIC outside Safari, or not decodable: sent as it is
  }
  try {
    const longest = Math.max(bitmap.width, bitmap.height);
    if (type !== 'image/heic' && longest <= max) return same;
    const scale = Math.min(1, max / longest);
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);
    const ctx = canvas.getContext('2d');
    if (!ctx) return same;
    ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    // PNG keeps transparency (screenshots); photos become JPEG
    const outType = type === 'image/png' ? 'image/png' : 'image/jpeg';
    const blob = await new Promise<Blob | null>((r) => canvas.toBlob(r, outType, options.quality ?? 0.85));
    if (!blob || (type !== 'image/heic' && blob.size >= file.size)) return same;
    const stem = fileName.replace(/\.[^.]+$/, '') || 'photo';
    return { file: blob, fileName: `${stem}.${outType === 'image/png' ? 'png' : 'jpg'}` };
  } finally {
    bitmap.close();
  }
}
